import { useMemo } from "react";

import { IAppLogoConfig, ISoccerStandingsTeamRank } from "../../../types";
import { appTournamentConfig, TOURNAMENT_ID } from "../config/tournament";

const isExactMatch = (
  teamName: string | undefined,
  searchName: string
): boolean => {
  return teamName?.toLowerCase().trim() === searchName;
};

// Word-boundary match — every word of the shorter name must appear as a whole
// word in the longer name. Avoids false positives like "Gent" matching
// "Argentina" via substring inclusion.
const isPartialMatch = (teamName: string, searchName: string): boolean => {
  const teamWords = teamName.toLowerCase().trim().split(/\s+/);
  const searchWords = searchName.split(/\s+/);
  const teamSet = new Set(teamWords);
  const searchSet = new Set(searchWords);
  return (
    teamWords.every((w) => searchSet.has(w)) ||
    searchWords.every((w) => teamSet.has(w))
  );
};

const createFallbackTeamConfig = (teamName: string): IAppLogoConfig => ({
  name: teamName,
  translatedName: teamName,
  translatedAbbrName: teamName,
  imgSrc: "/images/teamLogos/flag-fallback.png",
  statsColor: {
    home: "#ccc",
    away: "#fff",
  },
  textColor: {
    home: "#fff",
    away: "#000",
  },
});

export const getTeamInfoByCode = (code: string): IAppLogoConfig | null => {
  if (!code) return null;
  const teamLogosConfig = appTournamentConfig[TOURNAMENT_ID].teamLogos;
  if (!teamLogosConfig) return null;
  const normalizedCode = code.toUpperCase().trim();
  return teamLogosConfig.find(
    (team) => team.translatedAbbrName?.toUpperCase().trim() === normalizedCode
  ) ?? null;
};

export const getTeamInfo = (teamName: string): IAppLogoConfig => {
  if (!teamName) {
    return createFallbackTeamConfig("");
  }

  const teamLogosConfig = appTournamentConfig[TOURNAMENT_ID].teamLogos;
  if (!teamLogosConfig) {
    return createFallbackTeamConfig(teamName);
  }

  const normalizedSearchName = teamName.toLowerCase().trim();

  const team = teamLogosConfig.find((team) => {
    if (!team.name) return false;

    return (
      isExactMatch(team.name, normalizedSearchName) ||
      isExactMatch(team.otherName, normalizedSearchName) ||
      isPartialMatch(team.name, normalizedSearchName)
    );
  });

  return team ?? createFallbackTeamConfig(teamName);
};

export const useTeamLogos = (teamList: ISoccerStandingsTeamRank[]) => {
  const isClubTournament = appTournamentConfig[TOURNAMENT_ID].isClubTournament;
  return useMemo(() => {
    const flagList = teamList.map((team) => {
      const teamName = isClubTournament
        ? team.contestantClubName
        : team.contestantName;
      const logoInfo = getTeamInfo(teamName);

      return {
        ...team,
        translatedName: logoInfo.translatedName,
        logoImgSrc: logoInfo.imgSrc,
      };
    });
    return flagList;
  }, [teamList, isClubTournament]);
};
